// Client-side onboarding cache management with signed token verification
// Note: We can't verify signatures client-side (no access to secret), 
// so we trust the server signature but validate token structure and expiration

export interface OnboardingTokenData {
  userId: string;
  organizationId: string | null;
  hasPendingRequest: boolean;
  isAdmin: boolean;
  timestamp: number;
  expiresAt: number;
}

export interface SignedOnboardingToken {
  data: OnboardingTokenData;
  signature: string;
}

export type OnboardingStatus = boolean | 'pending';

const CACHE_KEY = 'nexus_onboarding_cache';

/**
 * Store signed onboarding token in session storage
 */
export function storeOnboardingToken(token: SignedOnboardingToken): void {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(token));
    // console.log('🔒 Onboarding token stored in cache'); // Removed repetitive log
  } catch (error) {
    console.warn('Failed to store onboarding token:', error);
  }
}

/**
 * Retrieve and validate cached onboarding token
 */
export function getCachedOnboardingToken(currentUserId: string): {
  isValid: boolean;
  isExpired: boolean;
  status: OnboardingStatus | null;
  token: SignedOnboardingToken | null;
} {
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (!cached) {
      return { isValid: false, isExpired: false, status: null, token: null };
    }

    const token: SignedOnboardingToken = JSON.parse(cached);
    
    // Validate token structure
    if (!token.data || !token.signature || typeof token.data !== 'object') {
      console.warn('🔒 Invalid token structure, clearing cache');
      clearOnboardingCache();
      return { isValid: false, isExpired: false, status: null, token: null };
    }

    const { data } = token;
    
    // Check if token is for current user
    if (data.userId !== currentUserId) {
      // console.warn('🔒 Token user mismatch, clearing cache');
      clearOnboardingCache();
      return { isValid: false, isExpired: false, status: null, token: null };
    }

    // Check expiration
    const now = Date.now();
    const isExpired = now > data.expiresAt;
    
    if (isExpired) {
      console.warn('🔒 Token expired, clearing cache');
      clearOnboardingCache();
      return { isValid: false, isExpired: true, status: null, token: null };
    }

    // Determine onboarding status from token data
    const status = getOnboardingStatusFromToken(data);
    
    console.log('🔒 Valid cached token found', {
      userId: data.userId,
      organizationId: data.organizationId,
      status,
      expiresIn: Math.round((data.expiresAt - now) / 1000 / 60) + ' minutes'
    });

    return { isValid: true, isExpired: false, status, token };
  } catch (error) {
    console.warn('🔒 Error reading cached token:', error);
    clearOnboardingCache();
    return { isValid: false, isExpired: false, status: null, token: null };
  }
}

/**
 * Clear onboarding cache
 */
export function clearOnboardingCache(): void {
  try {
    sessionStorage.removeItem(CACHE_KEY);
    // console.log('🔒 Onboarding cache cleared');
  } catch (error) {
    console.warn('Failed to clear onboarding cache:', error);
  }
}

/**
 * Determine onboarding completion status from token data
 */
function getOnboardingStatusFromToken(data: OnboardingTokenData): OnboardingStatus {
  // Known admin users always pass through
  if (data.isAdmin) {
    return true;
  }
  
  if (data.organizationId && data.organizationId !== null) {
    return true;
  }
  
  if (data.hasPendingRequest) {
    return 'pending';
  }
  
  return false;
}

/**
 * Check if we should trust the cached token for immediate UI rendering
 * This is a client-side validation - the server signature provides the real security
 */
export function shouldTrustCachedToken(currentUserId: string): {
  canTrust: boolean;
  status: OnboardingStatus | null;
} {
  const { isValid, status } = getCachedOnboardingToken(currentUserId);
  
  return {
    canTrust: isValid,
    status: isValid ? status : null
  };
}
